/**
 * /auth/login — password sign-in.
 *
 * 2026-05-20 (B-01 / B-02 + F-FIRM-006): magic-link sign-in was dropped on
 * Paul's call as Persona 06 (e2e closure). It had low real-world usage,
 * a broken dev-environment callback (Supabase Site URL mismatch), and a
 * persistent maintenance surface (RFC 2606 SMTP rejections, redirect-loop
 * bugs, expired-link confusion). Password + passkey is the only path now;
 * the WebAuthn / refresh-step-up flow handles aal2.
 *
 * Wires Supabase auth (Phase 10b):
 *   - supabase.auth.signInWithPassword. On success, the AuthProvider
 *     listener in main.tsx picks up the new session and the /app/_layout
 *     gate flips from "redirect → login" to "render outlet". We navigate
 *     to the role landing route once the listener has updated.
 *
 * Errors surface inline above the submit button via a small mapping helper
 * so library strings ("Invalid login credentials") don't reach the user
 * verbatim (closes F02-A-025).
 *
 * 2026-06-23 (auth polish): the password field and submit button moved to the
 * shared auth-controls (show/hide + Caps-Lock hint; spinner busy-state + a
 * brief success check before routing). The email input keeps the global
 * :focus-visible ring (the suppressing inline outline:none was removed).
 */

import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AuthLayout, useAuthHostBranding } from '../../components/layouts/auth-layout';
import { PasswordField, AuthSubmit } from '../../components/auth/auth-controls';
import { supabase } from '../../lib/supabase';
import { mapSupabaseAuthError } from '../../lib/auth-errors';
import { useTranslation } from '../../lib/i18n';
import { landingRouteForRole } from '../../lib/auth-landing';
import { prefetchDashboard } from '../../lib/api/prefetch-dashboard';
import { prefetchAdminDashboard } from '../../lib/api/prefetch-admin-dashboard';
import { queryClient } from '../../lib/query-client';
import type { UserRole } from '../../lib/api';

export const Route = createFileRoute('/auth/login')({
  component: LoginPage,
});

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // WHITE-LABEL (2026-06-12): on a firm host, the heading + footnote speak as
  // the firm — a client following their firm's invite must not be greeted by
  // a different company's name (see AuthLayout doc comment).
  const firm = useAuthHostBranding();
  // i18n (#42): the sign-in screen is the wired-end-to-end slice. On a firm
  // host the heading is firm-specific (white-label takes precedence over the
  // generic translated "Sign in to Elreign." string); everything else is
  // translated regardless of host.
  const { t } = useTranslation();

  return (
    <AuthLayout
      eyebrow={t('auth.eyebrow')}
      heading={firm ? `Sign in to ${firm.firmName}.` : t('auth.signInToElreign')}
      sub={t('auth.sub')}
    >
      <PasswordForm email={email} setEmail={setEmail} password={password} setPassword={setPassword} />

      {/* Tenant-portal note. On a firm host: say plainly whose portal this is.
          On the default host: generic copy — the subdomain shown is an
          illustrative pattern, never a real tenant: framed as "for example"
          so a settlor doesn't read it as their own address (FE-10 /
          Henry R2-011). */}
      <div
        style={{
          marginTop: 24,
          paddingTop: 20,
          borderTop: '1px solid var(--hairline)',
          fontSize: 12.5,
          color: 'var(--ink-3)',
          lineHeight: 1.55,
        }}
      >
        {firm ? (
          <>
            You&apos;re signing in to the secure client portal of{' '}
            <span style={{ color: 'var(--ink-2)', fontWeight: 500 }}>{firm.firmName}</span>,
            operated on the Elreign trust administration platform.
          </>
        ) : (
          <>
            Working with a trust admin firm on Elreign? Sign in via your firm&apos;s own
            branded login. Its address follows the pattern{' '}
            <span className="mono" style={{ color: 'var(--ink-2)' }}>
              your-firm.elreign.io
            </span>
            . Your administrator will have shared the exact link.
          </>
        )}
      </div>

      <div
        style={{
          marginTop: 18,
          fontSize: 13,
          color: 'var(--ink-3)',
        }}
      >
        {/* ELR-22 (21 Sep audit): was `--ink-2` on this `--ink-3` surrounding
            text — 1.62:1, and nothing but colour told the two apart. `--ink`
            plus an underline fixes both: a real non-colour cue, and enough
            contrast against the adjacent text to actually read as a distinct
            control (3.15:1, the WCAG 1.4.11 UI-component floor for a run of
            text set apart from its neighbour, not the 1.4.3 text-vs-page-
            background floor — the link's own contrast against the page
            background is 17.58:1, nowhere near the constraint). Guarded by
            src/styles/__tests__/elr-22-contrast.test.ts. */}
        {t('auth.noAccount')}{' '}
        <Link to="/auth/signup" style={{ color: 'var(--ink)', textDecoration: 'underline' }}>
          {t('auth.createOne')}
        </Link>
        .
      </div>
    </AuthLayout>
  );
}

interface PasswordFormProps {
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
}

function PasswordForm({ email, setEmail, password, setPassword }: PasswordFormProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || done) return;
    setBusy(true);
    setError(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setBusy(false);
      setError(t('auth.errEmailRequired'));
      return;
    }
    if (!/.+@.+\..+/.test(trimmed)) {
      setBusy(false);
      setError(t('auth.errEmailFormat'));
      return;
    }
    if (!password) {
      setBusy(false);
      setError(t('auth.errPasswordRequired'));
      return;
    }
    const { data, error: signInErr } = await supabase.auth.signInWithPassword({ email: trimmed, password });
    if (signInErr) {
      setBusy(false);
      setError(mapSupabaseAuthError(signInErr.message));
      return;
    }
    // Success: hold a brief brass→positive check (peak-end) before routing.
    // Route by role so firm staff land on the admin console (/admin), not the
    // single-trust B2C dashboard; the AuthProvider listener sets the new session
    // before this navigate fires so the target layout renders its outlet.
    setBusy(false);
    setDone(true);
    const role = data.session?.user?.app_metadata?.role as UserRole | undefined;
    const landing = landingRouteForRole(role);
    // Cold-start ruling (2026-07-15): the b2c dashboard's primary queries are
    // prefetched the moment sign-in succeeds — while this success check plays
    // and (usually) while the user then types their MFA code — so landing
    // paints from cache instead of paying the cold Function App. Guarded:
    // session exists (sign-in just succeeded), b2c landing surface only
    // (firm/admin/auditor land elsewhere), fire-and-forget (never blocks).
    if (data.session && landing === '/app/dashboard') {
      prefetchDashboard(queryClient);
    } else if (data.session && landing === '/admin/client-book') {
      // The firm half of the same ruling. Until 2026-08-11 only the b2c branch
      // above existed, so firm staff paid the full cold start on landing.
      prefetchAdminDashboard(queryClient, role);
    }
    window.setTimeout(() => {
      void navigate({ to: landing });
    }, 450);
  }

  const errorId = 'login-error';
  const describedBy = error !== null ? errorId : undefined;

  return (
    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <FieldLabel label={t('auth.emailLabel')} htmlFor="login-email">
        <input
          id="login-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.co.za"
          autoComplete="email"
          required
          aria-invalid={error !== null}
          aria-describedby={describedBy}
          style={inputStyle}
          disabled={busy}
        />
      </FieldLabel>

      <PasswordField
        id="login-password"
        label={t('auth.passwordLabel')}
        placeholder="Your password"
        value={password}
        onChange={setPassword}
        autoComplete="current-password"
        ariaInvalid={error !== null}
        ariaDescribedby={describedBy}
        disabled={busy || done}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -8 }}>
        <Link
          to="/auth/forgot"
          style={{ fontSize: 13, color: 'var(--primary-600)', textDecoration: 'none' }}
        >
          {t('auth.forgotPassword')}
        </Link>
      </div>

      {error !== null && <ErrorBox id={errorId} message={error} />}

      <AuthSubmit
        label={t('auth.signIn')}
        busyLabel={t('auth.signingIn')}
        busy={busy}
        done={done}
      />
    </form>
  );
}

// ── Shared sub-components ────────────────────────────────────────────────────

function FieldLabel({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>{label}</span>
      {children}
    </label>
  );
}

function ErrorBox({ id, message }: { id?: string; message: string }) {
  return (
    <div
      id={id}
      role="alert"
      aria-live="polite"
      style={{
        padding: '10px 12px',
        borderRadius: 8,
        background: 'rgba(220, 38, 38, 0.08)',
        border: '1px solid rgba(220, 38, 38, 0.3)',
        color: '#991B1B',
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      {message}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  borderRadius: 9,
  border: '1px solid var(--hairline-2)',
  background: 'var(--bg)',
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  fontSize: 15,
  boxSizing: 'border-box',
};
